import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config/api";
import Navbar from "./Navbar";
import Footer from "./header and footer/Footer";
import ImgUpload from "./ImgUpload";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGithub, faLinkedin } from "@fortawesome/free-brands-svg-icons";
import {
  faUser,
  faEnvelope,
  faPhone,
  faVenusMars,
  faBirthdayCake,
  faBriefcase,
  faMapMarkerAlt,
  faGraduationCap,
  faTrophy,
  faBookOpen,
  faCheckCircle,
  faSpinner,
  faLock,
  faEdit,
  faSave,
  faKey,
  faCamera,
  faShieldAlt
} from "@fortawesome/free-solid-svg-icons";

function Profile() {
  const navigate = useNavigate();
  const authToken = localStorage.getItem("token");
  const id = localStorage.getItem("id");
  const [userDetails, setUserDetails] = useState(null);
  const [profileImage, setProfileImage] = useState(localStorage.getItem("profileImage") || "");
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [completedCourses, setCompletedCourses] = useState([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [activeTab, setActiveTab] = useState("overview"); // "overview" | "edit" | "password"

  // Edit Profile Form State
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    phno: "",
    dob: "",
    gender: "Male",
    location: "",
    profession: "",
    linkedin_url: "",
    github_url: ""
  });

  // Password Form State
  const [passData, setPassData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [updatingPass, setUpdatingPass] = useState(false);
  const [updatingProfile, setUpdatingProfile] = useState(false);

  useEffect(() => {
    if (!authToken) {
      navigate("/login");
      return;
    }

    async function fetchUserDetails() {
      try {
        const response = await fetch(`${API_BASE_URL}/api/users/${id}`);
        if (!response.ok) throw new Error("Failed to fetch user details.");
        const data = await response.json();
        setUserDetails(data);
        setFormData({
          username: data.username || "",
          email: data.email || "",
          phno: data.phno || "",
          dob: data.dob || "",
          gender: data.gender || "Male",
          location: data.location || "",
          profession: data.profession || "",
          linkedin_url: data.linkedin_url || "",
          github_url: data.github_url || ""
        });
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    }

    async function fetchEnrolled() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/learning/${id}`);
        const data = await res.json();
        setEnrolledCourses(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Error fetching enrolled courses:", err);
      }
    }

    async function fetchPerformance() {
      try {
        const res = await fetch(`${API_BASE_URL}/api/assessments/perfomance/${id}`);
        const data = await res.json();
        if (Array.isArray(data)) {
          const bestScores = {};
          data.forEach((a) => {
            const cid = a.course_id;
            if (cid !== undefined) {
              bestScores[cid] = Math.max(bestScores[cid] || 0, a.marks || 0);
            }
          });
          const completed = Object.entries(bestScores)
            .filter(([, marks]) => marks >= 90)
            .map(([courseId, marks]) => ({ course_id: Number(courseId), marks }));
          setCompletedCourses(completed);
        }
      } catch (err) {
        console.error("Error fetching performance:", err);
      } finally {
        setLoadingProfile(false);
      }
    }

    fetchUserDetails();
    fetchEnrolled();
    fetchPerformance();
  }, [authToken, navigate, id]);

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target.result;
        localStorage.setItem("profileImage", imageData);
        setProfileImage(imageData);
        toast.success("Profile picture updated!");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePassChange = (e) => {
    const { name, value } = e.target;
    setPassData((prev) => ({ ...prev, [name]: value }));
  };

  // Submit Profile Info Update
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setUpdatingProfile(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        const updated = await response.json();
        setUserDetails(updated);
        localStorage.setItem("name", updated.username);
        localStorage.setItem("email", updated.email);
        toast.success("Profile details updated successfully!");
        setActiveTab("overview");
      } else {
        toast.error("Failed to update profile details.");
      }
    } catch (err) {
      console.error("Profile update error:", err);
      toast.error("Error connecting to server.");
    } finally {
      setUpdatingProfile(false);
    }
  };

  // Submit Password Change
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!passData.newPassword) {
      toast.error("Please enter a new password.");
      return;
    }
    if (passData.newPassword !== passData.confirmPassword) {
      toast.error("New password and confirm password do not match.");
      return;
    }
    setUpdatingPass(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/users/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...formData, password: passData.newPassword })
      });
      if (response.ok) {
        const updated = await response.json();
        setUserDetails(updated);
        toast.success("Password changed successfully!");
        setPassData({ currentPassword: "", newPassword: "", confirmPassword: "" });
        setActiveTab("overview");
      } else {
        toast.error("Failed to update password.");
      }
    } catch (err) {
      console.error("Password update error:", err);
      toast.error("Error connecting to server.");
    } finally {
      setUpdatingPass(false);
    }
  };

  const getCourseName = (courseId) => {
    const match = enrolledCourses.find((c) => (c.course_id || c.id) === courseId);
    return match ? (match.course_name || match.courseName || `Course #${courseId}`) : `Course #${courseId}`;
  };

  if (loadingProfile) {
    return (
      <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
        <Navbar page="profile" />
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "60vh", color: "#64748b" }}>
          <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "2rem", color: "#4f46e5", marginRight: "12px" }} />
          Loading profile...
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc", display: "flex", flexDirection: "column" }}>
      <Navbar page="profile" />

      <div style={{ flex: 1, maxWidth: "1050px", width: "100%", margin: "0 auto", padding: "40px 24px 80px" }}>

        {/* Header Card */}
        <div
          style={{
            background: "linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #4f46e5 100%)",
            borderRadius: "24px",
            padding: "36px 40px",
            marginBottom: "32px",
            display: "flex",
            alignItems: "center",
            gap: "32px",
            flexWrap: "wrap",
            boxShadow: "0 20px 40px -10px rgba(79,70,229,0.3)"
          }}
        >
          {/* Avatar Upload */}
          <div style={{ position: "relative", flexShrink: 0 }}>
            <div
              style={{
                width: "115px",
                height: "115px",
                borderRadius: "50%",
                border: "4px solid rgba(255,255,255,0.4)",
                overflow: "hidden",
                background: "rgba(255,255,255,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer"
              }}
            >
              <ImgUpload onChange={handleImageChange} src={profileImage} />
            </div>
            <span
              style={{
                position: "absolute",
                bottom: "4px",
                right: "4px",
                background: "#4f46e5",
                color: "#fff",
                borderRadius: "50%",
                width: "28px",
                height: "28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.75rem",
                boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
              }}
              title="Change Profile Picture"
            >
              <FontAwesomeIcon icon={faCamera} />
            </span>
          </div>

          {/* User Basic Info */}
          <div style={{ flex: 1, minWidth: "220px" }}>
            <h1 style={{ color: "#fff", fontSize: "1.9rem", fontWeight: 800, margin: "0 0 6px", fontFamily: "'Outfit', sans-serif" }}>
              {userDetails?.username || "Student Account"}
            </h1>
            <p style={{ color: "#a5b4fc", fontSize: "0.95rem", margin: "0 0 16px", fontWeight: "600" }}>
              <FontAwesomeIcon icon={faBriefcase} style={{ marginRight: "8px" }} />
              {userDetails?.profession || "Learner / Specialist"}
            </p>

            <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
              <div
                style={{
                  background: "rgba(255,255,255,0.12)",
                  borderRadius: "20px",
                  padding: "6px 16px",
                  color: "#fff",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "7px"
                }}
              >
                <FontAwesomeIcon icon={faBookOpen} style={{ color: "#a5b4fc" }} />
                {enrolledCourses.length} Enrolled
              </div>
              <div
                style={{
                  background: "rgba(16,185,129,0.25)",
                  borderRadius: "20px",
                  padding: "6px 16px",
                  color: "#fff",
                  fontSize: "0.85rem",
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: "7px"
                }}
              >
                <FontAwesomeIcon icon={faTrophy} style={{ color: "#34d399" }} />
                {completedCourses.length} Completed
              </div>
            </div>
          </div>

          {/* Social Icons */}
          <div style={{ display: "flex", gap: "12px", flexShrink: 0 }}>
            {userDetails?.linkedin_url && (
              <a
                href={userDetails.linkedin_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  textDecoration: "none"
                }}
              >
                <FontAwesomeIcon icon={faLinkedin} style={{ fontSize: "1.2rem" }} />
              </a>
            )}
            {userDetails?.github_url && (
              <a
                href={userDetails.github_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  width: "44px",
                  height: "44px",
                  borderRadius: "50%",
                  background: "rgba(255,255,255,0.15)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  textDecoration: "none"
                }}
              >
                <FontAwesomeIcon icon={faGithub} style={{ fontSize: "1.2rem" }} />
              </a>
            )}
          </div>
        </div>

        {/* Tab Navigation Controls */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            marginBottom: "28px",
            borderBottom: "2px solid #e2e8f0",
            paddingBottom: "8px",
            flexWrap: "wrap"
          }}
        >
          <button
            onClick={() => setActiveTab("overview")}
            style={{
              padding: "10px 22px",
              borderRadius: "10px",
              border: "none",
              background: activeTab === "overview" ? "#4f46e5" : "transparent",
              color: activeTab === "overview" ? "#fff" : "#64748b",
              fontWeight: 700,
              fontSize: "0.92rem",
              cursor: "pointer",
              transition: "all 0.2s ease"
            }}
          >
            <FontAwesomeIcon icon={faUser} style={{ marginRight: "8px" }} />
            Overview & Details
          </button>

          <button
            onClick={() => setActiveTab("edit")}
            style={{
              padding: "10px 22px",
              borderRadius: "10px",
              border: "none",
              background: activeTab === "edit" ? "#4f46e5" : "transparent",
              color: activeTab === "edit" ? "#fff" : "#64748b",
              fontWeight: 700,
              fontSize: "0.92rem",
              cursor: "pointer",
              transition: "all 0.2s ease"
            }}
          >
            <FontAwesomeIcon icon={faEdit} style={{ marginRight: "8px" }} />
            Edit Profile
          </button>

          <button
            onClick={() => setActiveTab("password")}
            style={{
              padding: "10px 22px",
              borderRadius: "10px",
              border: "none",
              background: activeTab === "password" ? "#4f46e5" : "transparent",
              color: activeTab === "password" ? "#fff" : "#64748b",
              fontWeight: 700,
              fontSize: "0.92rem",
              cursor: "pointer",
              transition: "all 0.2s ease"
            }}
          >
            <FontAwesomeIcon icon={faLock} style={{ marginRight: "8px" }} />
            Security & Password
          </button>
        </div>

        {/* Tab 1: Overview */}
        {activeTab === "overview" && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "28px" }}>
            {/* Personal Details */}
            <div
              style={{
                background: "#fff",
                borderRadius: "20px",
                border: "1px solid #e2e8f0",
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.04)",
                overflow: "hidden"
              }}
            >
              <div style={{ padding: "20px 24px", borderBottom: "1px solid #f1f5f9", background: "#fafbff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: "1.05rem", display: "flex", alignItems: "center", gap: "8px" }}>
                  <FontAwesomeIcon icon={faUser} style={{ color: "#4f46e5" }} />
                  Personal Information
                </h3>
                <button
                  onClick={() => setActiveTab("edit")}
                  style={{
                    background: "#e0e7ff",
                    color: "#4f46e5",
                    border: "none",
                    padding: "6px 14px",
                    borderRadius: "8px",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    cursor: "pointer"
                  }}
                >
                  Edit Details
                </button>
              </div>

              <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "18px" }}>
                {[
                  { icon: faEnvelope, label: "Email Address", value: userDetails?.email, color: "#4f46e5" },
                  { icon: faPhone, label: "Phone Number", value: userDetails?.phno, color: "#06b6d4" },
                  { icon: faVenusMars, label: "Gender", value: userDetails?.gender, color: "#ec4899" },
                  { icon: faBirthdayCake, label: "Date of Birth", value: userDetails?.dob, color: "#f59e0b" },
                  { icon: faMapMarkerAlt, label: "Location", value: userDetails?.location, color: "#10b981" },
                  { icon: faBriefcase, label: "Profession", value: userDetails?.profession, color: "#8b5cf6" },
                ].map(({ icon, label, value, color }) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                    <div
                      style={{
                        width: "38px",
                        height: "38px",
                        borderRadius: "12px",
                        background: `${color}15`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                      }}
                    >
                      <FontAwesomeIcon icon={icon} style={{ color, fontSize: "0.95rem" }} />
                    </div>
                    <div>
                      <div style={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                        {label}
                      </div>
                      <div style={{ fontSize: "0.95rem", color: "#0f172a", fontWeight: 600 }}>{value || "Not specified"}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Courses Overview */}
            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              {/* Enrolled Courses */}
              <div
                style={{
                  background: "#fff",
                  borderRadius: "20px",
                  border: "1px solid #e2e8f0",
                  boxShadow: "0 4px 6px -1px rgba(0,0,0,0.04)",
                  overflow: "hidden"
                }}
              >
                <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", background: "#fafbff", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <h3 style={{ margin: 0, fontWeight: 800, color: "#0f172a", fontSize: "1rem", display: "flex", alignItems: "center", gap: "8px" }}>
                    <FontAwesomeIcon icon={faGraduationCap} style={{ color: "#4f46e5" }} />
                    Enrolled Courses
                  </h3>
                  <span style={{ background: "#e0e7ff", color: "#4f46e5", borderRadius: "20px", padding: "3px 12px", fontSize: "0.8rem", fontWeight: 700 }}>
                    {enrolledCourses.length}
                  </span>
                </div>
                <div style={{ padding: "18px 24px" }}>
                  {enrolledCourses.length === 0 ? (
                    <p style={{ color: "#94a3b8", fontSize: "0.88rem", margin: 0 }}>No enrolled courses yet.</p>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                      {enrolledCourses.map((c, i) => {
                        const cId = c.course_id || c.id;
                        const name = c.course_name || c.courseName || `Course #${cId}`;
                        const isCourseDone = completedCourses.some((comp) => comp.course_id === cId);
                        return (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              padding: "12px 16px",
                              background: isCourseDone ? "#f0fdf4" : "#f8fafc",
                              borderRadius: "12px",
                              border: `1px solid ${isCourseDone ? "#bbf7d0" : "#f1f5f9"}`
                            }}
                          >
                            <span style={{ fontSize: "0.9rem", color: "#334155", fontWeight: 600 }}>{name}</span>
                            {isCourseDone ? (
                              <span style={{ color: "#10b981", fontSize: "0.8rem", fontWeight: 700, display: "flex", alignItems: "center", gap: "6px" }}>
                                <FontAwesomeIcon icon={faCheckCircle} /> Completed
                              </span>
                            ) : (
                              <span style={{ color: "#64748b", fontSize: "0.8rem", fontWeight: 600 }}>In Progress</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Completed Courses */}
              {completedCourses.length > 0 && (
                <div
                  style={{
                    background: "linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)",
                    borderRadius: "20px",
                    border: "1.5px solid #bbf7d0",
                    overflow: "hidden"
                  }}
                >
                  <div style={{ padding: "18px 24px", borderBottom: "1px solid #bbf7d0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <h3 style={{ margin: 0, fontWeight: 800, color: "#065f46", fontSize: "1rem", display: "flex", alignItems: "center", gap: "8px" }}>
                      <FontAwesomeIcon icon={faTrophy} style={{ color: "#10b981" }} />
                      Verified Certifications
                    </h3>
                    <span style={{ background: "#10b981", color: "#fff", borderRadius: "20px", padding: "3px 12px", fontSize: "0.8rem", fontWeight: 700 }}>
                      {completedCourses.length}
                    </span>
                  </div>
                  <div style={{ padding: "18px 24px", display: "flex", flexDirection: "column", gap: "10px" }}>
                    {completedCourses.map((c, i) => (
                      <div
                        key={i}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          padding: "12px 16px",
                          background: "rgba(255,255,255,0.8)",
                          borderRadius: "12px",
                          border: "1px solid #bbf7d0"
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <FontAwesomeIcon icon={faTrophy} style={{ color: "#10b981", fontSize: "0.95rem" }} />
                          <span style={{ fontSize: "0.9rem", color: "#065f46", fontWeight: 700 }}>
                            {getCourseName(c.course_id)}
                          </span>
                        </div>
                        <span style={{ background: "#10b981", color: "#fff", borderRadius: "20px", padding: "4px 12px", fontSize: "0.78rem", fontWeight: 800 }}>
                          Score: {c.marks}%
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 2: Edit Profile */}
        {activeTab === "edit" && (
          <div
            style={{
              background: "#fff",
              borderRadius: "20px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 6px -1px rgba(0,0,0,0.04)",
              padding: "32px",
              maxWidth: "700px",
              margin: "0 auto"
            }}
          >
            <h3 style={{ fontSize: "1.2rem", fontWeight: 800, color: "#0f172a", marginBottom: "20px" }}>
              Edit Personal Details
            </h3>

            <form onSubmit={handleProfileSubmit} style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px" }}>
                <div>
                  <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", marginBottom: "6px", display: "block" }}>
                    Full Name / Username
                  </label>
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "0.9rem" }}
                    required
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", marginBottom: "6px", display: "block" }}>
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "0.9rem" }}
                    required
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px" }}>
                <div>
                  <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", marginBottom: "6px", display: "block" }}>
                    Phone Number
                  </label>
                  <input
                    type="text"
                    name="phno"
                    value={formData.phno}
                    onChange={handleInputChange}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "0.9rem" }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", marginBottom: "6px", display: "block" }}>
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    name="dob"
                    value={formData.dob}
                    onChange={handleInputChange}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "0.9rem" }}
                  />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px" }}>
                <div>
                  <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", marginBottom: "6px", display: "block" }}>
                    Gender
                  </label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "0.9rem" }}
                  >
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", marginBottom: "6px", display: "block" }}>
                    Location / Country
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "0.9rem" }}
                  />
                </div>
              </div>

              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", marginBottom: "6px", display: "block" }}>
                  Profession / Role
                </label>
                <input
                  type="text"
                  name="profession"
                  value={formData.profession}
                  onChange={handleInputChange}
                  style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "0.9rem" }}
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px" }}>
                <div>
                  <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", marginBottom: "6px", display: "block" }}>
                    LinkedIn URL
                  </label>
                  <input
                    type="text"
                    name="linkedin_url"
                    value={formData.linkedin_url}
                    onChange={handleInputChange}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "0.9rem" }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", marginBottom: "6px", display: "block" }}>
                    GitHub URL
                  </label>
                  <input
                    type="text"
                    name="github_url"
                    value={formData.github_url}
                    onChange={handleInputChange}
                    style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "0.9rem" }}
                  />
                </div>
              </div>

              <div style={{ marginTop: "12px", display: "flex", gap: "12px" }}>
                <button
                  type="submit"
                  disabled={updatingProfile}
                  style={{
                    background: "linear-gradient(135deg, #4f46e5 0%, #3730a3 100%)",
                    color: "#fff",
                    border: "none",
                    padding: "12px 28px",
                    borderRadius: "10px",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    cursor: "pointer"
                  }}
                >
                  <FontAwesomeIcon icon={faSave} style={{ marginRight: "8px" }} />
                  {updatingProfile ? "Saving..." : "Save Profile Details"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Tab 3: Security & Change Password */}
        {activeTab === "password" && (
          <div
            style={{
              background: "#fff",
              borderRadius: "20px",
              border: "1px solid #e2e8f0",
              boxShadow: "0 4px 6px -1px rgba(0,0,0,0.04)",
              padding: "32px",
              maxWidth: "550px",
              margin: "0 auto"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "16px" }}>
              <FontAwesomeIcon icon={faShieldAlt} style={{ color: "#4f46e5", fontSize: "1.3rem" }} />
              <h3 style={{ fontSize: "1.2rem", fontWeight: 800, color: "#0f172a", margin: 0 }}>
                Change Account Password
              </h3>
            </div>
            <p style={{ color: "#64748b", fontSize: "0.88rem", marginBottom: "24px" }}>
              Update your security password to protect your account.
            </p>

            <form onSubmit={handlePasswordSubmit} style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", marginBottom: "6px", display: "block" }}>
                  New Password
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={passData.newPassword}
                  onChange={handlePassChange}
                  style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "0.9rem" }}
                  required
                />
              </div>

              <div>
                <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#334155", marginBottom: "6px", display: "block" }}>
                  Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passData.confirmPassword}
                  onChange={handlePassChange}
                  style={{ width: "100%", padding: "10px 14px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "0.9rem" }}
                  required
                />
              </div>

              <div style={{ marginTop: "8px" }}>
                <button
                  type="submit"
                  disabled={updatingPass}
                  style={{
                    background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                    color: "#fff",
                    border: "none",
                    padding: "12px 28px",
                    borderRadius: "10px",
                    fontWeight: 700,
                    fontSize: "0.9rem",
                    cursor: "pointer"
                  }}
                >
                  <FontAwesomeIcon icon={faKey} style={{ marginRight: "8px" }} />
                  {updatingPass ? "Updating Password..." : "Update Password"}
                </button>
              </div>
            </form>
          </div>
        )}

      </div>
      <Footer />
    </div>
  );
}

export default Profile;
